import React, { useCallback, useState } from 'react';
import {
  classes,
  PrimaryButton,
  TransparentButton,
} from '@bloomberg/media-ui-core';
import { PlusThinStroke } from '@bloomberg/media-ui-icons';

import styles from './CreateWatchlist.module.css';

export type CreateWatchlistProps = {
  onCreate: (watchlistName: string) => void;
  showAddNewWatchlist: boolean;
  setShowAddNewWatchlist: (showAddNewWatchlist: boolean) => void;
};

const ENTER_KEY = 'Enter';

enum ERRORS {
  MAX_LENGTH = 'Must be no longer than 30 characters.',
}

const isTooLong = (name: string) => {
  return name.length > 30;
};

export const CreateWatchlist = (props: CreateWatchlistProps) => {
  const [watchlistName, setWatchlistName] = useState('');
  const [errorMessage, setErrorMessage] = useState<ERRORS | null>(null);

  const { onCreate, showAddNewWatchlist, setShowAddNewWatchlist } = props;

  const handleOnNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const name = e.target.value;

      if (isTooLong(name)) {
        setErrorMessage(ERRORS.MAX_LENGTH);

        return;
      }

      setErrorMessage(null);
      setWatchlistName(e.target.value);
    },
    []
  );

  const handleOnCreate = useCallback(() => {
    onCreate(watchlistName);
    setWatchlistName('');
  }, [onCreate, watchlistName]);

  const handleOnKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === ENTER_KEY) {
        handleOnCreate();
      }
    },
    [handleOnCreate]
  );

  const close = useCallback(() => {
    setShowAddNewWatchlist(false);
    setErrorMessage(null);
  }, [setShowAddNewWatchlist, setErrorMessage]);
  const open = useCallback(
    () => setShowAddNewWatchlist(true),
    [setShowAddNewWatchlist]
  );

  const hasError = Boolean(errorMessage);
  const disableCreateButton = watchlistName.length === 0 || hasError;
  const inputStyles = classes([
    styles.nameInput,
    { [styles.nameInputError]: hasError },
  ]);
  const inputTitleStyles = classes([
    styles.inputTitle,
    { [styles.inputTitleError]: hasError },
  ]);

  return (
    <div className={styles.createWatchlist}>
      {showAddNewWatchlist ? (
        <>
          <div className={styles.inputContainer}>
            <p className={inputTitleStyles}>Watchlist name</p>
            <input
              autoFocus
              className={inputStyles}
              aria-label="watchlist-name"
              onChange={handleOnNameChange}
              onKeyDown={disableCreateButton ? undefined : handleOnKeyDown}
              placeholder="Enter list name"
              type="text"
            />
            {errorMessage && (
              <p className={styles.errorMessage}>{errorMessage}</p>
            )}
          </div>
          <div className={styles.buttonsContainer}>
            <TransparentButton
              className={styles.cancelButton}
              onClick={close}
              title="cancel"
            >
              Cancel
            </TransparentButton>
            <PrimaryButton
              className={styles.createButton}
              disabled={disableCreateButton}
              onClick={handleOnCreate}
              size="small"
              version="phoenix"
              title="create"
            >
              Create
            </PrimaryButton>
          </div>
        </>
      ) : (
        <button
          className={styles.createNewListContainer}
          onClick={open}
          type="button"
        >
          <PlusThinStroke />
          <p className={styles.createNewListText}>Create a new list</p>
        </button>
      )}
    </div>
  );
};
